import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMsal } from "@azure/msal-react";

import {
  type AuthProviderConfig,
  type AuthProviderId,
  type B2cConfig,
  type EntraConfig,
  clearSelectedProvider,
  getSelectedProvider,
  loadEnabledProviders,
  setSelectedProvider,
} from "./authProviders";
import {
  clearStoredTokens,
  getFreshAccessToken,
  getStoredTokens,
  readB2cAccountLabel,
  ropcLogin,
  setStoredTokens,
  type B2cTokens,
} from "./b2cClient";
import { jwtExpiryMs, jwtUserOid } from "./jwt";
import { deriveIsLoading } from "./authGate";
import { setAuthenticatedUser } from "../telemetry/appInsights";
import { useSharedAuthBridge } from "../bridge/AuthBridgeContext";
import { isEmbeddedInExtension } from "../bridge/types";

const EMBEDDED = isEmbeddedInExtension();

// Refresh the access token this many ms before its `exp` claim. Covers
// clock skew and keeps requests from racing an expiry that lands mid-flight.
const TOKEN_REFRESH_LEAD_MS = 60_000;

/**
 * Unified auth state across all three providers + both runtime modes
 * (standalone web vs extension-embedded). Consumers read `accessToken` /
 * `isAuthenticated` / `isLoading` without caring which provider is active;
 * Apollo reads the live value via `getToken()`.
 *
 * Login methods are separated by shape — Entra is interactive (MSAL popup in
 * standalone, chrome.identity round-trip via the AuthBridge in embedded),
 * while the two B2C providers take email/password credentials for ROPC.
 */
export interface AuthState {
  enabledProviders: AuthProviderConfig[];
  selectedProvider: AuthProviderId | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;
  accountLabel: string | null;
  loginWithEntra: () => Promise<void>;
  loginWithB2c: (
    providerId: "pathways-b2c" | "provider-portal-b2c",
    email: string,
    password: string,
  ) => Promise<void>;
  /**
   * Adopt a B2C session minted elsewhere (the side-panel sign-in popup) as if
   * `loginWithB2c` had just succeeded: stash the tokens in THIS window's
   * storage, select the provider, and publish the access token.
   */
  completeB2cSignIn: (
    providerId: "pathways-b2c" | "provider-portal-b2c",
    tokens: B2cTokens,
  ) => void;
  logout: () => Promise<void>;
  /** Stable getter — for Apollo authLink, which runs outside React. */
  getToken: () => string | null;
}

const AuthCtx = createContext<AuthState | null>(null);

export function useAuthContext(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuthContext must be inside <AuthProvider>");
  return ctx;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const enabledProviders = useMemo(() => loadEnabledProviders(), []);
  const entraCfg = enabledProviders.find(
    (p): p is EntraConfig => p.id === "entra",
  );
  const pathwaysCfg = enabledProviders.find(
    (p): p is B2cConfig => p.id === "pathways-b2c",
  );
  const providerPortalCfg = enabledProviders.find(
    (p): p is B2cConfig => p.id === "provider-portal-b2c",
  );

  const bridge = EMBEDDED ? useSharedAuthBridge() : null;
  // useMsal is guaranteed to be mounted in standalone mode (App wires the
  // providers). In embedded we never call it.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const msal = EMBEDDED ? null : useMsal();

  const [selectedProvider, setSelectedProviderState] =
    useState<AuthProviderId | null>(() => getSelectedProvider());
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoadingExplicit, setIsLoadingExplicit] = useState(false);
  // False until the first token-resolution attempt settles. Keeps the UI in a
  // "resolving" (spinner) state so it never flashes the login screen before we
  // know whether a valid token exists. See deriveIsLoading.
  const [initialResolved, setInitialResolved] = useState(false);

  const tokenRef = useRef<string | null>(null);
  tokenRef.current = accessToken;

  const b2cCfgFor = useCallback(
    (id: "pathways-b2c" | "provider-portal-b2c") =>
      id === "pathways-b2c" ? pathwaysCfg : providerPortalCfg,
    [pathwaysCfg, providerPortalCfg],
  );

  /**
   * Pull a fresh token for the currently selected provider. Called on mount,
   * when the selection changes, and when MSAL's account list settles after a
   * popup login.
   */
  const refreshToken = useCallback(async () => {
    if (!selectedProvider) {
      setAccessToken(null);
      return;
    }

    if (EMBEDDED) {
      // Extension mode: Entra runs through chrome.identity via the bridge;
      // B2C still goes direct from the iframe (CSP lets iframe origins reach
      // b2clogin.com).
      if (selectedProvider === "entra") {
        setAccessToken(bridge?.accessToken ?? null);
        return;
      }
      const cfg = b2cCfgFor(selectedProvider);
      if (!cfg) {
        setAccessToken(null);
        return;
      }
      setAccessToken(await getFreshAccessToken(cfg));
      return;
    }

    // Standalone web.
    if (selectedProvider === "entra") {
      if (!entraCfg || !msal) {
        setAccessToken(null);
        return;
      }
      const account = msal.accounts[0] ?? null;
      if (!account) {
        setAccessToken(null);
        return;
      }
      try {
        const res = await msal.instance.acquireTokenSilent({
          account,
          scopes: entraCfg.apiScopes,
        });
        setAccessToken(res.accessToken);
      } catch {
        setAccessToken(null);
      }
      return;
    }

    const cfg = b2cCfgFor(selectedProvider);
    if (!cfg) {
      setAccessToken(null);
      return;
    }
    setAccessToken(await getFreshAccessToken(cfg));
  }, [selectedProvider, bridge?.accessToken, entraCfg, msal, b2cCfgFor]);

  useEffect(() => {
    void refreshToken().finally(() => setInitialResolved(true));
  }, [refreshToken]);

  // Embedded recovery: if the extension shell holds a valid Entra token but this
  // iframe has no persisted provider selection (e.g. the SPA's localStorage was
  // cleared while the extension's chrome.storage token survived), adopt Entra so
  // we honor the existing session instead of prompting for a fresh sign-in.
  useEffect(() => {
    if (EMBEDDED && !selectedProvider && bridge?.accessToken) {
      setSelectedProvider("entra");
      setSelectedProviderState("entra");
    }
  }, [selectedProvider, bridge?.accessToken]);

  // Schedule a refresh `TOKEN_REFRESH_LEAD_MS` before the current token
  // expires. If the token is already past (or near) its `exp` when we see
  // it — e.g. resumed from sleep — the timeout fires immediately. This keeps
  // care-management-gql from ever verifying a request with an expired
  // bearer, as long as the silent-refresh path still works.
  useEffect(() => {
    if (!accessToken) return;
    const expMs = jwtExpiryMs(accessToken);
    if (expMs === null) return;
    const delay = Math.max(0, expMs - Date.now() - TOKEN_REFRESH_LEAD_MS);
    const timer = window.setTimeout(() => {
      void refreshToken();
    }, delay);
    return () => window.clearTimeout(timer);
  }, [accessToken, refreshToken]);

  // Sync App Insights authenticated user context whenever the access token
  // changes. Extracts the opaque OID/sub claim — never PHI. On logout
  // (accessToken → null) clears the context so future anonymous events are
  // not attributed to the previous user.
  useEffect(() => {
    setAuthenticatedUser(jwtUserOid(accessToken) ?? undefined);
  }, [accessToken]);

  // Resolve app-vs-login only once auth is definitively known. Covers the
  // initial-resolution window (all providers) and the bridge→AuthContext mirror
  // lag (embedded Entra) so we never flash the login screen — or the app — while
  // a valid token is still loading. See deriveIsLoading.
  const isLoading = deriveIsLoading({
    initialResolved,
    embedded: EMBEDDED,
    provider: selectedProvider,
    bridgeLoading: bridge?.isLoading ?? false,
    bridgeToken: bridge?.accessToken ?? null,
    accessToken,
    isLoadingExplicit,
  });

  const loginWithEntra = useCallback(async () => {
    if (!entraCfg) throw new Error("Entra is not an enabled provider");

    if (EMBEDDED) {
      // Sidepanel runs chrome.identity and posts AUTH_TOKEN back via the
      // bridge; we just signal our selection and wait for the token event.
      setSelectedProvider("entra");
      setSelectedProviderState("entra");
      bridge?.login();
      return;
    }

    if (!msal) throw new Error("MSAL not initialised");
    // Full-page redirect rather than a pop-up. Pop-ups are unreliable on
    // mobile browsers, and because our redirectUri is the SPA itself, the
    // pop-up booted the whole app and the router (`/` → `/members`) stripped
    // MSAL's response hash before it could be read — surfacing as
    // hash_empty_error (most often on mobile). The redirect flow completes on
    // the next boot: main.tsx runs handleRedirectPromise() *before* React
    // mounts, so the hash is consumed before the router can touch it, and the
    // active provider is recorded there. (The extension never reaches this
    // branch — it auths via chrome.identity through the bridge, see EMBEDDED
    // above, and is unaffected by this change.)
    setIsLoadingExplicit(true);
    try {
      await msal.instance.loginRedirect({ scopes: entraCfg.apiScopes });
      // loginRedirect navigates away; code below does not run on success.
    } catch (e) {
      // Redirect failed to even start (e.g. interaction_in_progress) — clear
      // the pending state so LoginPage can surface a friendly error.
      setIsLoadingExplicit(false);
      throw e;
    }
  }, [bridge, entraCfg, msal]);

  const loginWithB2c = useCallback(
    async (
      id: "pathways-b2c" | "provider-portal-b2c",
      email: string,
      password: string,
    ) => {
      const cfg = b2cCfgFor(id);
      if (!cfg) throw new Error(`Provider ${id} is not enabled`);
      setIsLoadingExplicit(true);
      try {
        const tokens = await ropcLogin(cfg, email, password);
        setSelectedProvider(id);
        setSelectedProviderState(id);
        setAccessToken(tokens.access_token);
      } finally {
        setIsLoadingExplicit(false);
      }
    },
    [b2cCfgFor],
  );

  const completeB2cSignIn = useCallback(
    (id: "pathways-b2c" | "provider-portal-b2c", tokens: B2cTokens) => {
      if (!b2cCfgFor(id)) throw new Error(`Provider ${id} is not enabled`);
      setStoredTokens(id, tokens);
      setSelectedProvider(id);
      setSelectedProviderState(id);
      setAccessToken(tokens.access_token);
    },
    [b2cCfgFor],
  );

  const logout = useCallback(async () => {
    const wasProvider = selectedProvider;
    clearSelectedProvider();
    setSelectedProviderState(null);
    setAccessToken(null);

    // The Provider Portal B2C stash (b2cClient's "provider-portal-b2c" key)
    // is written OUTSIDE the primary-provider flow by the Create-Auth
    // seamless-login modal's `ropcLogin` call (see auth/ppToken.ts /
    // PpLoginModal), independent of which provider is primary. It is NOT
    // namespaced by identity in storage, so it MUST be cleared on EVERY
    // logout — not just when it was the primary provider — otherwise it
    // survives a logout and a different person signing in on this
    // browser/profile could inherit the previous user's PP session. This is
    // belt-and-suspenders alongside ppToken.ts's own identity-claim check;
    // either alone would be a bypass risk, both together are not.
    clearStoredTokens("provider-portal-b2c");

    if (wasProvider === "pathways-b2c") {
      clearStoredTokens(wasProvider);
      return;
    }
    if (wasProvider === "provider-portal-b2c") {
      return; // already cleared above
    }

    if (wasProvider === "entra") {
      if (EMBEDDED) {
        bridge?.logout();
      } else if (msal) {
        try {
          await msal.instance.logoutPopup();
        } catch {
          /* ignore — user may have cancelled the popup */
        }
      }
    }
  }, [bridge, msal, selectedProvider]);

  const accountLabel = useMemo(() => {
    if (!selectedProvider) return null;
    if (EMBEDDED && selectedProvider === "entra") {
      return bridge?.account?.username ?? bridge?.account?.name ?? null;
    }
    if (!EMBEDDED && selectedProvider === "entra") {
      return msal?.accounts[0]?.username ?? null;
    }
    // B2C — display whichever provider is active. `readB2cAccountLabel`
    // prefers the email the session was AUTHENTICATED with over anything
    // decoded out of the id_token; see its doc comment for why that order is
    // load-bearing (AM-5930: the Provider Portal policy emits no email claim,
    // so decoding first surfaced an opaque Key ID as the profile label).
    return readB2cAccountLabel(getStoredTokens(selectedProvider));
  }, [bridge?.account, msal?.accounts, selectedProvider]);

  const value: AuthState = {
    enabledProviders,
    selectedProvider,
    isAuthenticated: !!accessToken,
    isLoading,
    accessToken,
    accountLabel,
    loginWithEntra,
    loginWithB2c,
    completeB2cSignIn,
    logout,
    getToken: () => tokenRef.current,
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}
